import React, { useState, useEffect } from 'react';
import { patientService } from '../modules/patient/services/patientService';
import toast from 'react-hot-toast';

/**
 * Home Care Booking Section Component
 * 
 * This component handles Home Care bookings for Aged Care patients.
 * It should be conditionally rendered when patient_type === 'Aged Care'
 * 
 * Props:
 * - patientData: Object containing patient information (must have id, user_id, patient_type)
 * - onBookingSuccess: Callback function when booking is created successfully
 */
const HomeCareBookingSection = ({ patientData, onBookingSuccess }) => {
  const [scheduleType, setScheduleType] = useState('');
  const [frequencyOption, setFrequencyOption] = useState('');
  const [customDays, setCustomDays] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Calculate frequency days based on selection
  const getFrequencyDays = () => {
    if (scheduleType !== 'Recurring') return null;
    if (frequencyOption === 'custom') return parseInt(customDays) || 0;
    return parseInt(frequencyOption) || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!scheduleType) {
      toast.error('Please select schedule type');
      return;
    }
    
    if (scheduleType === 'Recurring') {
      const days = getFrequencyDays();
      if (!days || days < 1) {
        toast.error('Please select or enter a valid frequency');
        return;
      }
    }
    
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    
    if (!selectedTime) {
      toast.error('Please select a time');
      return;
    }

    try {
      setLoading(true);

      const bookingData = {
        user_id: patientData.user_id,
        patient_id: patientData.id,
        reason: reason || 'Home Care - Vitals Monitoring',
        appointment_type: 'Home Care',
        schedule_type: scheduleType,
        appointment_date: selectedDate,
        appointment_time: selectedTime,
        frequency_days: getFrequencyDays()
      };

      console.log('🏠 Creating Home Care booking:', bookingData);

      const response = await patientService.createHomeCareBooking(bookingData);
      
      console.log('✅ Home Care booking created:', response);

      // Show success message
      if (scheduleType === 'One Time') {
        toast.success(`Home Care booking created for ${selectedDate}`);
      } else {
        toast.success(`${response.bookings_created} Home Care bookings created`);
      }

      // Reset form
      setScheduleType('');
      setFrequencyOption('');
      setCustomDays('');
      setSelectedDate(null);
      setSelectedTime('');
      setReason('');

      // Call success callback
      if (onBookingSuccess) {
        onBookingSuccess(response);
      }

    } catch (error) {
      console.error('❌ Error creating Home Care booking:', error);
      toast.error(error.message || 'Failed to create Home Care booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-lg border-2 border-purple-200">
      <h3 className="text-lg font-bold text-purple-800 mb-4">
        🏠 Home Care Booking (Aged Care)
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason (Optional)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Daily vitals monitoring"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Schedule Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Schedule Type *
          </label>
          <select
            value={scheduleType}
            onChange={(e) => {
              setScheduleType(e.target.value);
              if (e.target.value !== 'Recurring') {
                setFrequencyOption('');
                setCustomDays('');
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
          >
            <option value="">Select Schedule Type</option>
            <option value="One Time">One Time</option>
            <option value="Recurring">Recurring</option>
          </select>
        </div>

        {/* Frequency (only for Recurring) */}
        {scheduleType === 'Recurring' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Frequency *
            </label>
            <select
              value={frequencyOption}
              onChange={(e) => {
                setFrequencyOption(e.target.value);
                if (e.target.value !== 'custom') {
                  setCustomDays('');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Select Frequency</option>
              <option value="2">2 days</option>
              <option value="3">3 days</option>
              <option value="4">4 days</option>
              <option value="5">5 days</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        )}

        {/* Custom Days Input */}
        {scheduleType === 'Recurring' && frequencyOption === 'custom' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Days *
            </label>
            <input
              type="number"
              min="1"
              max="30"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              placeholder="Enter number of days (1-30)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
        )}

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date *
          </label>
          <input
            type="date"
            value={selectedDate || ''}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
          />
        </div>

        {/* Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time *
          </label>
          <input
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
          />
        </div>

        {/* Info Box */}
        <div className="bg-purple-100 border border-purple-300 rounded-md p-3">
          <p className="text-sm text-purple-800">
            <strong>ℹ️ Note:</strong> Home Care bookings do not require doctor or clinic selection.
            {scheduleType === 'Recurring' && getFrequencyDays() > 0 && (
              <span className="block mt-1">
                This will create <strong>{getFrequencyDays()} bookings</strong> starting from {selectedDate}.
              </span>
            )}
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Creating...
            </span>
          ) : (
            `Create Home Care Booking${scheduleType === 'Recurring' ? 's' : ''}`
          )}
        </button>
      </form>
    </div>
  );
};

export default HomeCareBookingSection;

